import { AgGridReact } from 'ag-grid-react';
import { Breadcrumb, Button, ConfigProvider, Pagination, Tooltip } from 'antd';
import React, { useEffect, useMemo, useState } from 'react'
import { NodataTable } from '../../../components/NoDataTable';
import { CustomLoadingOverlayTable, dataPagination, getWidthFromHeaderV2, renderValueAsJSX } from '../../../until';
import viVN from 'antd/locale/vi_VN';
import { formatNumber } from '../../../services/mixin';
import { ColDef } from 'ag-grid-community';
import { NO_DATA } from '../../../env';
import Layout from '../../../Layout';
import useDebounce from '../../../hook/useDebounce';
import useLoading from '../../../hook/useLoading';
import useToast from '../../../hook/useToast';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import { exportCustomerListByCompanyUnit, exportInforCustomerReportAddress, exportInforCustomerReportGenderBussiness, exportInforCustomerReportJob, exportInforCustomerReportOnVehicleDistributionByAge, exportReportCampaignCustomerAddress, exportReportCampaignCustomerJob, exportReportCampaignDemographics, exportReportCampaignVehicleDistributionByAge, exportReportVoucherCustomerList, getCustomerListByCompanyUnit, getInforCustomerReportAddress, getInforCustomerReportCampaignCustomerAddress, getInforCustomerReportCampaignCustomerJob, getInforCustomerReportCampaignDemographics, getInforCustomerReportCampaignVehicleDistributionByAge, getInforCustomerReportGenderBussiness, getInforCustomerReportJob, getInforCustomerReportOnVehicleDistributionByAge, getInforCustomerReportVoucherCustomerList } from '../../../api/reportCustomer/reportCustomerApi';
import { useSelector } from 'react-redux';
import { selectorInfoUser } from '../../../store/slices/infoUserSlice';
import dayjs from 'dayjs';
import { getListFieldsByCustomer, ListField } from '../../../api/Customer_V2/customerApi_V2';
import { FIELDTYPE, NO_VIEW } from '../../../services/data';
import type { FlattenedField } from '../../CustomersV2/indexV2';
import CustomerFeilds from '../../CustomersV2/Drawer/CustomerFeilds';
import '../../Customers/CustomerList.scss';



// Row đã được "làm phẳng" (flatten) từ danh sách fields động của khách hàng
// (chỉ dùng cho bảng thường - không áp dụng cho bảng unitIdVoucher)
export interface ReportCustomerFieldRow {
    id: number;
    [fieldCode: string]: any;
}

// Làm phẳng dữ liệu thô trả về từ API báo cáo khách hàng (không phải voucher)
// giữ nguyên metadata gốc (code, updatedAt, permissionMenu, ...) và map field.view ? field.value : NO_VIEW
export const flattenCustomerReportRows = (data: any[]): ReportCustomerFieldRow[] => {
    return (data ?? []).map((item) => {
        const fields = item?.fields ?? [];

        const fieldValues = fields.reduce((acc: Record<string, any>, field: any) => {
            acc[field.fieldCode] = field.view ? field.value : NO_VIEW
            return acc
        }, {} as Record<string, any>)

        return {
            ...item,
            id: item.id,
            ...fieldValues,
        };
    });
};


export interface CustomerRowVoucher {
    id: number;

    // Khách hàng
    employee: string;
    phone: string;

    // Xe
    chassisNo: string;
    numberPlate: string;

    // Hóa đơn
    repairInvoiceNo: string;
    transactionInvoiceNo: string;

    // Campaign / Voucher
    campaignName: string;
    voucherStatus: string;
    voucherIssuanceDate: string;
    voucherConversionDate: string;
    voucherConversionUnit: string;
    voucherName: string;

    // Màu trạng thái voucher
    voucherStatusColors?: {
        bg?: string;
        border?: string;
        text?: string;
    };
}


export const mapCustomerVoucherData = (
    data: any[]
): CustomerRowVoucher[] => {
    return data.map(item => {
        const fields = item.fields ?? [];

        const getField = (fieldCode: string) =>
            fields.find(
                (field: any) => field.fieldCode === fieldCode
            );
        return {
            id: Number(item.id),
            // Khách hàng
            employee:
                getField('employee')?.value || '',
            phone:
                getField('cus_tel')?.value || '',
            // Xe
            chassisNo:
                getField('own_chassis_no')?.value || '',
            numberPlate:
                getField('own_number_plate')?.value || '',
            // Hóa đơn
            repairInvoiceNo:
                getField('repair_invoice_no')?.value || '',
            transactionInvoiceNo:
                getField('trans_invoice_no')?.value || '',
            // Campaign
            campaignName:
                getField('name_campaign')?.value || '',
            // Voucher
            voucherStatus:
                getField('status_voucher')?.value || '',
            voucherIssuanceDate:
                getField('voucher_issuance_date')?.value || '',
            voucherConversionDate:
                getField('voucher_conversion')?.value || '',
            voucherConversionUnit:
                getField('unit_voucher_conversion')?.value || '',
            voucherName:
                getField('name_voucher')?.value || '',
            voucherStatusColors:
                getField('status_voucher')
                    ?.additionalData
                    ?.colors,
        };
    });
};

const ReportCustomerCardList = () => {
    const { unitTemp } = useSelector(selectorInfoUser)

    const pushToast = useToast()
    const pushLoading = useLoading()
    const location = useLocation();
    const navigate = useNavigate();

    const { title, vehicleType, ageVehicle, typeGender, typeBussiness, statusMarital, dataFilter, dataFilterCampaign, age: ageCampaign, dataFilterVoucher, provinceId, jobId, firstPage, reportType, businessCheckVoucher, yearVoucher, genderVoucher, statusMaritalVoucher, ageGroupVoucher, provinceIdVoucher, professionIdVoucher, unitIdVoucher, typeCompanyUnit, restoreReportPagination } = location?.state ?? {}

    // Bảng theo đơn vị quy đổi voucher (unitIdVoucher) giữ nguyên hành vi cũ (cột tĩnh, map riêng)
    const isVoucherMode = unitIdVoucher !== undefined

    const [loadingTable, setLoadingTable] = useState(false)
    const [isExporting, setIsExporting] = useState(false)

    const [dataReportCustomerCardList, setDataReportCustomerCardList] = useState<ReportCustomerFieldRow[]>([])
    const [dataReportCustomerVoucherCardList, setDataReportCustomerVoucherCardList] = useState<CustomerRowVoucher[]>([])
    const [pagination, setPagination] = useState({
        current: restoreReportPagination?.current ?? 1,
        pageSize: restoreReportPagination?.pageSize ?? 10,
        total: 0
    });

    // Cấu hình trường hiển thị động (chỉ áp dụng cho bảng thường - không phải bảng unitIdVoucher)
    const [isSettingShow, setSettingShow] = useState(false)
    const [dataFilterActive, setDataFilterActive] = useState<FlattenedField[]>([])
    const [dataFilterNoActive, setDataFilterNoActive] = useState<ListField[]>([])

    // Cột động cho bảng thường, sinh theo danh sách trường đang active (đúng thứ tự người dùng sắp xếp)
    const normalColumnDefs = useMemo<ColDef<ReportCustomerFieldRow>[]>(() => {
        return dataFilterActive.map((field) => ({
            headerName: field.fieldLabel,
            field: field.fieldCode,
            colId: field.fieldCode,
            minWidth: getWidthFromHeaderV2(field.fieldLabel),
            cellRenderer: (params: any) => {
                if (field.fieldCode !== 'cus_name') {
                    return renderValueAsJSX(params.value, field.fieldCode);
                }

                const { id } = params.data;
                const fullName = params.value;
                return (
                    <Tooltip
                        placement="right"
                        color="white"
                        title={
                            <span className="custom-page-css" style={{ color: "#000", textTransform: 'capitalize' }}>
                                {fullName}
                            </span>
                        }
                    >
                        <Link
                            style={{ color: "#1890ff", textTransform: 'capitalize' }}
                            to="/customer/list/edit"
                            state={{
                                firstPage: location.pathname,
                                id,
                                title,
                                typeGender,
                                typeBussiness,
                                dataFilter,
                                backState: {
                                    ...location.state,
                                    restoreReportPagination: {
                                        current: pagination.current,
                                        pageSize: pagination.pageSize
                                    }
                                }
                            }}
                        >
                            {fullName}
                        </Link>
                    </Tooltip>
                );
            },
        }));
    }, [dataFilterActive, location, title, typeGender, typeBussiness, dataFilter, pagination.current, pagination.pageSize]);

    const columnDefsVoucher = useMemo<ColDef<CustomerRowVoucher>[]>(() => [
        {
            headerName: 'Tên voucher',
            field: 'voucherName',
            minWidth: getWidthFromHeaderV2('Tên voucher'),
        },

        {
            headerName: 'Trạng thái voucher',
            field: 'voucherStatus',
            minWidth: getWidthFromHeaderV2('Trạng thái voucher'),

            cellRenderer: (params: any) => {
                const { voucherStatus, voucherStatusColors } = params?.data || {};

                return (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 8px',
                            height: '24px',
                            borderRadius: 4,

                            backgroundColor:
                                voucherStatusColors?.bg || '#F2F4F7',

                            border: `1px solid ${voucherStatusColors?.border || '#D0D5DD'
                                }`,

                            color:
                                voucherStatusColors?.text || '#475467',
                        }}
                    >
                        {voucherStatus}
                    </span>
                );
            },
        },
        {
            headerName: 'Số điện thoại',
            field: 'phone',
            minWidth: getWidthFromHeaderV2('Số điện thoại'),
        },

        {
            headerName: 'Số khung (VIN)',
            field: 'chassisNo',
            minWidth: getWidthFromHeaderV2('Số khung (VIN)'),
        },

        {
            headerName: 'Biển số xe',
            field: 'numberPlate',
            minWidth: getWidthFromHeaderV2('Biển số xe'),
        },

        {
            headerName: 'Số hóa đơn sửa chữa',
            field: 'repairInvoiceNo',
            minWidth: getWidthFromHeaderV2('Số hóa đơn sửa chữa'),
        },

        {
            headerName: 'Số hóa đơn',
            field: 'transactionInvoiceNo',
            minWidth: getWidthFromHeaderV2('Số hóa đơn'),
        },

        {
            headerName: 'Tên chiến dịch',
            field: 'campaignName',
            minWidth: getWidthFromHeaderV2('Tên chiến dịch'),
        },



        {
            headerName: 'Ngày phát hành voucher',
            field: 'voucherIssuanceDate',
            minWidth: getWidthFromHeaderV2('Ngày phát hành voucher'),

            valueFormatter: (params) => {
                if (
                    !params.value ||
                    params.value === 'Không xác định'
                ) {
                    return params.value || '';
                }

                return dayjs(
                    params.value,
                    'YYYY/MM/DD'
                ).format('DD/MM/YYYY');
            },
        },

        {
            headerName: 'Ngày quy đổi voucher',
            field: 'voucherConversionDate',
            minWidth: getWidthFromHeaderV2('Ngày quy đổi voucher'),

            valueFormatter: (params) => {
                if (
                    !params.value ||
                    params.value === 'Không xác định'
                ) {
                    return params.value || '';
                }

                return dayjs(
                    params.value,
                    'DD-MM-YYYY'
                ).format('DD/MM/YYYY');
            },
        },

        {
            headerName: 'Đơn vị quy đổi voucher',
            field: 'voucherConversionUnit',
            minWidth: getWidthFromHeaderV2('Đơn vị quy đổi voucher'),
        },
        {
            headerName: 'Tên nhân viên',
            field: 'employee',
            minWidth: getWidthFromHeaderV2('Tên nhân viên'),
        },
    ], [location]);
    type CustomerGridRow = ReportCustomerFieldRow | CustomerRowVoucher;
    const gridRowData: CustomerGridRow[] = isVoucherMode
        ? dataReportCustomerVoucherCardList
        : dataReportCustomerCardList;

    const gridColumnDefs: ColDef<CustomerGridRow>[] = isVoucherMode
        ? columnDefsVoucher as ColDef<CustomerGridRow>[]
        : normalColumnDefs as ColDef<CustomerGridRow>[];

    // Danh sách trường (đang active) dùng để build cột động - chỉ dùng cho bảng thường
    const gListFieldsActive = async () => {
        pushLoading(true)
        const result = await getListFieldsByCustomer(FIELDTYPE.CUSTOMER, 1)
        if (result?.status) {
            const flattenedFields = result.data.flatMap(choice =>
                choice.fields.map(field => ({
                    ...field,
                    parentId: choice.id,
                }))
            );
            setDataFilterActive(flattenedFields)
            gListFieldsNoActive()
        }
        pushLoading(false)
    }

    // Danh sách trường chưa active - dùng cho Drawer cài đặt cột hiển thị
    const gListFieldsNoActive = async () => {
        const result = await getListFieldsByCustomer(FIELDTYPE.CUSTOMER, 0)
        if (result?.status) {
            setDataFilterNoActive(result.data)
        }
    }

    // Lưu cấu hình cột hiển thị: đóng drawer, toast, cập nhật danh mục cột rồi refresh báo cáo theo trang hiện tại
    const onSaveDataFields = (
        iSsuccess?: boolean,
        message?: string,
        dataFilterActiveTmp?: FlattenedField[],
        dataFilterNoActiveTmp?: ListField[]
    ) => {
        if (iSsuccess) {
            setSettingShow(false)
            pushToast(message, "success")
            setDataFilterActive(dataFilterActiveTmp ?? [])
            setDataFilterNoActive(dataFilterNoActiveTmp ?? [])
            gInforCustomerReportDetail(pagination.current, pagination.pageSize)
        } else {
            pushToast(message, "warning")
        }
    }

    const onCloseDataFields = () => {
        setSettingShow(false)
    }

    const gInforCustomerReportDetail = useDebounce(async (pageCurent: number = 1, pageSize: number = 10) => {
        setLoadingTable(true)

        switch (firstPage) {
            case '/analysis-report/customers':
                // Handle the specific case for customer analysis report
                const { unitId, businessCheck, gender, job, province, carBrand, group: groupCustomer, age } = dataFilter ?? {}

                switch (true) {
                    case typeGender !== undefined || typeBussiness !== undefined:
                        const result = await getInforCustomerReportGenderBussiness(
                            pageSize, pageCurent, undefined,
                            unitTemp, unitId,
                            businessCheck, gender, job,
                            province, carBrand,
                            groupCustomer, age,
                            typeBussiness, typeGender
                        );
                        if (result?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(result.data))
                            setPagination({ current: pageCurent, total: result.total, pageSize: pageSize })
                        } else {
                            pushToast(result?.message, "error", 2000)
                        }
                        break;
                    case provinceId !== undefined:
                        const resultProvince = await getInforCustomerReportAddress(
                            pageSize, pageCurent, undefined,
                            unitTemp, unitId,
                            businessCheck, gender, job,
                            province, carBrand,
                            groupCustomer, age,
                            provinceId
                        );
                        if (resultProvince?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(resultProvince.data))
                            setPagination({ current: pageCurent, total: resultProvince.total, pageSize: pageSize })
                        } else {
                            pushToast(resultProvince?.message, "error", 2000)
                        }
                        break;
                    case jobId !== undefined:
                        const resultJob = await getInforCustomerReportJob(
                            pageSize, pageCurent, undefined,
                            unitTemp, unitId,
                            businessCheck, gender, job,
                            province, carBrand,
                            groupCustomer, age,
                            jobId
                        );
                        if (resultJob?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(resultJob.data))
                            setPagination({ current: pageCurent, total: resultJob.total, pageSize: pageSize })
                        } else {
                            pushToast(resultJob?.message, "error", 2000)
                        }
                        break;

                    case vehicleType !== undefined || ageVehicle !== undefined:
                        const resultVehicleDistributionByAge = await getInforCustomerReportOnVehicleDistributionByAge(
                            pageSize, pageCurent, undefined,
                            ageVehicle,
                            vehicleType,
                            unitTemp,
                            unitId,
                            businessCheck,
                            gender,
                            job,
                            province,
                            carBrand,
                            groupCustomer,
                        );
                        if (resultVehicleDistributionByAge?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(resultVehicleDistributionByAge.data))
                            setPagination({ current: pageCurent, total: resultVehicleDistributionByAge.total, pageSize: pageSize })
                        } else {
                            pushToast(resultVehicleDistributionByAge?.message, "error", 2000)
                        }
                        break;
                    default:
                        break;
                }
                break;
            case '/analysis-report/marketing-campaigns':
                const { campaign: listCampaign, brandName: listBrand, status: statusFilter, dateCreate, dateSend } = dataFilterCampaign ?? {}

                switch (true) {
                    case typeGender !== undefined || typeBussiness !== undefined || statusMarital !== undefined:
                        const result = await getInforCustomerReportCampaignDemographics(
                            pageSize, pageCurent, undefined, unitTemp,
                            listCampaign, listBrand, statusFilter, dateCreate, dateSend,
                            typeGender, typeBussiness, statusMarital,
                        );
                        if (result?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(result.data))
                            setPagination({ current: pageCurent, total: result.total, pageSize: pageSize })
                        } else {
                            pushToast(result?.message, "error", 2000)
                        }
                        break;
                    case ageCampaign !== undefined:
                        const resultAge = await getInforCustomerReportCampaignVehicleDistributionByAge(
                            //                  limit, pageNumber, search, unitIdDefault,
                            // listCampaign, listBrand, status, dateCreate, dateSend,
                            // unitId, channelId, gender, businessCheck, province, groupCustomer, carBrand, job,
                            // age,
                            pageSize, pageCurent, undefined, unitTemp,
                            listCampaign, listBrand, statusFilter, dateCreate, dateSend,
                            undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                            ageCampaign,
                        );
                        if (resultAge?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(resultAge.data))
                            setPagination({ current: pageCurent, total: resultAge.total, pageSize: pageSize })
                        } else {
                            pushToast(resultAge?.message, "error", 2000)
                        }
                        break;
                    case provinceId !== undefined:
                        const resultProvince = await getInforCustomerReportCampaignCustomerAddress(
                            pageSize, pageCurent, undefined, unitTemp,
                            listCampaign, listBrand, statusFilter, dateCreate, dateSend,
                            provinceId
                        );
                        if (resultProvince?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(resultProvince.data))
                            setPagination({ current: pageCurent, total: resultProvince.total, pageSize: pageSize })
                        } else {
                            pushToast(resultProvince?.message, "error", 2000)
                        }
                        break;
                    case jobId !== undefined:
                        const resultJob = await getInforCustomerReportCampaignCustomerJob(
                            pageSize, pageCurent, undefined, unitTemp,
                            listCampaign, listBrand, statusFilter, dateCreate, dateSend,
                            jobId
                        );
                        if (resultJob?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(resultJob.data))
                            setPagination({ current: pageCurent, total: resultJob.total, pageSize: pageSize })
                        } else {
                            pushToast(resultJob?.message, "error", 2000)
                        }
                        break;
                    default:
                        break;
                }
                break;
            case '/analysis-report/voucher-overview':
                const { unitIds, campaignIds: listCampaignIds, dateStart: dateCreateVoucher, dateEnd: dateSendVoucher, type, typeVoucherId } = dataFilterVoucher ?? {}

                switch (true) {
                    case reportType !== undefined:
                        const result = await getInforCustomerReportVoucherCustomerList(
                            pageSize, pageCurent, reportType, unitTemp, unitIds,
                            listCampaignIds, type, typeVoucherId, dateCreateVoucher, dateSendVoucher, yearVoucher, businessCheckVoucher, genderVoucher, statusMaritalVoucher, ageGroupVoucher, provinceIdVoucher, professionIdVoucher
                        );
                        if (result?.status) {
                            setDataReportCustomerCardList(flattenCustomerReportRows(result.data))
                            setPagination({ current: pageCurent, total: result.total, pageSize: pageSize })
                        } else {
                            pushToast(result?.message, "error", 2000)
                        }
                        break;
                    case unitIdVoucher !== undefined:
                        const resultCompanyUnit = await getCustomerListByCompanyUnit(
                            pageSize, pageCurent, unitIdVoucher,
                            listCampaignIds, type, typeVoucherId, dateCreateVoucher, dateSendVoucher
                        );
                        if (resultCompanyUnit?.status) {
                            setDataReportCustomerVoucherCardList(mapCustomerVoucherData(resultCompanyUnit.data))
                            setPagination({ current: pageCurent, total: resultCompanyUnit.total, pageSize: pageSize })
                        } else {
                            pushToast(resultCompanyUnit?.message, "error", 2000)
                        }
                        break;

                }


                // Handle the specific case for customer analysis report
                break;
        }


        setLoadingTable(false)
    }, 300)

    const handleExportExcel = async () => {
        setIsExporting(true)
        switch (firstPage) {
            case '/analysis-report/customers':
                const { unitId, businessCheck, gender, job, province, carBrand, group: groupCustomer, age } = dataFilter ?? {}

                switch (true) {
                    case typeGender !== undefined || typeBussiness !== undefined:
                        await exportInforCustomerReportGenderBussiness(
                            undefined,
                            unitTemp, unitId,
                            businessCheck, gender, job,
                            province, carBrand,
                            groupCustomer, age,
                            typeBussiness, typeGender
                        )
                        break;
                    case provinceId !== undefined:
                        await exportInforCustomerReportAddress(
                            undefined, provinceId,
                            unitTemp, unitId,
                            businessCheck, gender, job,
                            province, carBrand,
                            groupCustomer, age

                        );

                        break;
                    case jobId !== undefined:
                        await exportInforCustomerReportJob(
                            undefined, jobId,
                            unitTemp, unitId,
                            businessCheck, gender, job,
                            province, carBrand,
                            groupCustomer, age,

                        );
                        break;
                    case vehicleType !== undefined || ageVehicle !== undefined:
                        await exportInforCustomerReportOnVehicleDistributionByAge(
                            undefined,
                            ageVehicle,
                            vehicleType,
                            unitTemp,
                            unitId,
                            businessCheck,
                            gender,
                            job,
                            province,
                            carBrand,
                            groupCustomer,

                        );
                        break;
                }
                break;
            case '/analysis-report/marketing-campaigns':
                const { campaign: listCampaign, brandName: listBrand, status: statusFilter, dateCreate, dateSend } = dataFilterCampaign ?? {}

                switch (true) {
                    case typeGender !== undefined || typeBussiness !== undefined || statusMarital !== undefined:
                        await exportReportCampaignDemographics(
                            undefined, unitTemp,
                            listCampaign, listBrand, statusFilter, dateCreate, dateSend,
                            typeGender, typeBussiness, statusMarital,
                        )
                        break;
                    case ageCampaign !== undefined:
                        await exportReportCampaignVehicleDistributionByAge(
                            unitTemp,
                            listCampaign, listBrand, statusFilter, dateCreate, dateSend,
                            ageCampaign
                        )
                        break;
                    case provinceId !== undefined:
                        await exportReportCampaignCustomerAddress(
                            unitTemp,
                            listCampaign, listBrand, statusFilter, dateCreate, dateSend,
                            provinceId
                        )
                        break;
                    case jobId !== undefined:
                        await exportReportCampaignCustomerJob(
                            unitTemp,
                            listCampaign, listBrand, statusFilter, dateCreate, dateSend,
                            jobId
                        )
                        break;
                }
                break;
            case '/analysis-report/voucher-overview':
                const { unitIds, campaignIds: listCampaignIds, dateStart: dateCreateVoucher, dateEnd: dateSendVoucher, type, typeVoucherId } = dataFilterVoucher ?? {}
                switch (true) {
                    case reportType !== undefined:
                        await exportReportVoucherCustomerList(
                            reportType, unitTemp, unitIds,
                            listCampaignIds, type, typeVoucherId, dateCreateVoucher, dateSendVoucher, yearVoucher, businessCheckVoucher, genderVoucher, statusMaritalVoucher, ageGroupVoucher, provinceIdVoucher, professionIdVoucher
                        )
                        break;
                    case unitIdVoucher !== undefined:
                        await exportCustomerListByCompanyUnit(
                            unitIdVoucher,
                            listCampaignIds, type, typeVoucherId, dateCreateVoucher, dateSendVoucher
                        )
                        break;
                }

                break;
        }

        setIsExporting(false)
    }

    const handleChangePagination = (current: number, pageSize: number) => {
        setPagination((prev) => ({ ...prev, current: current, pageSize }))
        gInforCustomerReportDetail(current, pageSize)
    }


    useEffect(() => {
        if (title === undefined) {
            // navigate(-1)
        } else {
            gInforCustomerReportDetail(
                restoreReportPagination?.current ?? 1,
                restoreReportPagination?.pageSize ?? 10
            )
            // Cài đặt trường hiển thị động chỉ áp dụng cho bảng thường (không phải bảng unitIdVoucher)
            if (!isVoucherMode) {
                gListFieldsActive()
            }
        }
    }, [location])


    return (
        <div className='card-issuance-list'>
            <Layout>

                {
                    !isVoucherMode && isSettingShow ? (
                        <CustomerFeilds
                            isSettingShow={isSettingShow}
                            dataFilterActive={dataFilterActive}
                            dataFilterNoActive={dataFilterNoActive}

                            onSaveDataFields={onSaveDataFields}
                            onClose={onCloseDataFields}
                        />
                    ) : null
                }

                <Breadcrumb
                    items={[
                        {
                            title: 'THACO AUTO CRM',
                        },
                        {
                            title: <a href="/">Báo cáo phân tích</a>,
                        },
                        {
                            title: 'Chiến dịch CSKH/ Marketing',
                        },
                        {
                            title: 'Danh sách khách hàng',
                        },
                    ]}
                />

                <div className=' custom-page-css flex items-center gap-2'>
                    <Link to={firstPage} className="icon-svg pb-1">
                        <svg width="1em" height="1em" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14.6564 7.80321H4.04305L10.3955 2.37463C10.4971 2.28713 10.4354 2.12463 10.3012 2.12463H8.69582C8.62508 2.12463 8.55796 2.14963 8.50536 2.19428L1.65045 8.04963C1.58767 8.10321 1.53732 8.16944 1.50281 8.24385C1.4683 8.31825 1.45044 8.39909 1.45044 8.48088C1.45044 8.56268 1.4683 8.64352 1.50281 8.71792C1.53732 8.79233 1.58767 8.85856 1.65045 8.91213L8.54526 14.8032C8.57247 14.8264 8.60512 14.8389 8.63959 14.8389H10.2993C10.4336 14.8389 10.4953 14.6746 10.3937 14.5889L4.04305 9.16035H14.6564C14.7363 9.16035 14.8016 9.09606 14.8016 9.01749V7.94606C14.8016 7.86749 14.7363 7.80321 14.6564 7.80321Z" fill="currentColor" fillOpacity="0.88" />
                        </svg>
                    </Link>
                    <span className='title-page'>Danh sách khách hàng ({formatNumber(pagination.total)})</span>
                </div>

                <div className="custom-page-css w-full flex items-center justify-between pb-2">
                    <span>{title}</span>
                    <div className='flex items-center gap-2'>
                        {
                            !isVoucherMode ? (
                                <div
                                    className='w-6 h-6 d-flex justify-center align-center cursor-pointer border-D9D9D9 rounded-2'
                                    onClick={() => setSettingShow(true)}
                                >
                                    <SettingOutlined className='text-sm' />
                                </div>
                            ) : null
                        }
                        <Button
                            icon={<DownloadOutlined />}
                            loading={isExporting}
                            onClick={handleExportExcel}
                        >
                            Xuất báo cáo
                        </Button>
                    </div>
                </div>
                <div className="t-table-block custom-page-css flex flex-col flex-1 justify-between">
                    <div className="ag-theme-alpine empty-test-td custom-table-ag-v2" style={{ width: '100%' }}>
                        <AgGridReact<CustomerGridRow>
                            domLayout="autoHeight"

                            rowData={gridRowData}
                            columnDefs={gridColumnDefs}

                            defaultColDef={{
                                resizable: true,
                                sortable: false,
                                flex: 1,

                                valueFormatter: (params: any) =>
                                    params.value && String(params.value).trim() !== ""
                                        ? params.value
                                        : NO_DATA,
                            }}

                            loading={loadingTable}

                            getRowId={(params) =>
                                params.data.id.toString()
                            }

                            suppressDragLeaveHidesColumns

                            loadingOverlayComponent={CustomLoadingOverlayTable}
                            overlayNoRowsTemplate={NodataTable}

                            rowHeight={55}

                            getRowStyle={(params: any) => ({
                                backgroundColor:
                                    params?.rowIndex % 2
                                        ? '#f7f9fc'
                                        : '#ffffff',
                            })}
                        />
                    </div>
                    <ConfigProvider locale={viVN}>
                        <div className="pagination-block flex justify-center">

                            <Pagination
                                total={pagination.total}
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                showSizeChanger
                                itemRender={(page, type, originalElement) => {
                                    // Chỉ custom khi là nút số trang
                                    if (type === 'page') {
                                        return <span>{formatNumber(page)}</span>;
                                    }
                                    // prev / next / jump-prev / jump-next giữ nguyên
                                    return originalElement;
                                }}
                                showTotal={(total) => `Tổng cộng ${dataPagination(pagination.total, pagination.pageSize, pagination.current).totalPage} trang`}
                                locale={{ items_per_page: 'trang' }}
                                onChange={handleChangePagination}
                            />
                        </div>
                    </ConfigProvider>
                </div>


            </Layout >
        </div >
    )
}
export default ReportCustomerCardList
